/** Canvas history engine (Tier 3 Group C Phase 3). Replaces the
 *  shared `useUndoRedo` substrate the image editor used previously.
 *  Owned + driven by `useCanvasHistory`; exposed as a plain class
 *  so the data model is testable in isolation and so the History
 *  panel can subscribe to mutations independently of the canvas
 *  hook's render cycle.
 *
 *  Design contract:
 *
 *    - **200-step cap** by default (up from 50). Manual snapshots
 *      are PINNED — they're exempt from the cap. The cap only
 *      trims non-manual entries from the OLDEST end.
 *
 *    - **Linear history with fork-on-edit semantics.** Click-to-jump
 *      moves the cursor without altering the timeline. The next
 *      `push()` after a jump discards everything past the cursor
 *      (matches Photoshop / VS Code / every other "undo tree" tool's
 *      pragmatic default). Preserving the abandoned branch as a
 *      forkable tree is deliberately out of scope; the storage and
 *      UI cost don't pay back in normal editing flows.
 *
 *    - **Labels are first-class.** Every entry carries a display
 *      string for the History panel — "Brush", "Move", "Layer
 *      added", or the user's typed name for manual snapshots. The
 *      hook layer fills the label automatically when a registry
 *      command is in flight (see `setPendingLabel` in
 *      `useCanvasHistory`); explicit `push(..., { label })` is
 *      always honored.
 *
 *    - **State payload is opaque to this engine.** Today every entry
 *      stores a full `{ canvasJSON, extra }` snapshot — fine for
 *      moderate sessions; the cap holds the memory footprint
 *      bounded. The follow-up optimization (diff-based deltas
 *      between periodic snapshots) is a pure internal change; the
 *      `push` / `undo` / `redo` / `jumpTo` contract returns whole
 *      snapshots either way, so callers don't need to know.
 *
 *  No React, no Fabric. Tests instantiate directly. */

export interface HistorySnapshot<TExtra = unknown> {
  canvasJSON: object;
  extra?: TExtra;
}

/** Metadata for one history step, WITHOUT its state payload —
 *  this is the shape the History panel UI binds to. State stays
 *  inside the engine; the panel needs only labels + ids + flags. */
export interface HistoryEntry {
  /** Stable id used by `jumpTo` + as the React key in the panel.
   *  Format `h-<N>` where N is a per-engine monotonic counter; the
   *  format is treated as opaque by every caller except `restore`
   *  (which parses it to re-seed the counter after a session
   *  reload — see implementation note there). */
  id: string;
  /** Display label, e.g. "Move", "Brush", or a user-supplied name
   *  for manual snapshots. */
  label: string;
  /** Set when the entry was created by a registered command —
   *  used by the panel to render an info row (or future "replay"
   *  affordance) and by the upcoming Action Recorder to group
   *  consecutive command dispatches. Absent for snapshots produced
   *  by direct canvas mutations (drag, paint strokes). */
  commandId?: string;
  /** True for user-named "manual snapshot" anchors — Photoshop's
   *  Snapshots panel. Pinned (cap-exempt) and visually distinct. */
  isManual: boolean;
  /** Wall-clock timestamp (ms since epoch) at create time. Used
   *  by the panel's tooltip + future "history age" displays. */
  timestamp: number;
}

/** Internal storage row — metadata plus the state payload the
 *  engine applies on undo / redo / jump. The panel never sees this
 *  shape directly; `getEntries()` strips the payload. */
export interface HistoryEntryWithState<TExtra = unknown> extends HistoryEntry {
  state: HistorySnapshot<TExtra>;
}

/** Default cap — exported so the future "History size" settings
 *  control reads the same constant. Hard floor: 2 (anything lower
 *  prevents the always-current + at-least-one-prior pattern). */
export const HISTORY_CAP = 200;

/** Separate cap on PINNED manual snapshots. Manual snapshots are
 *  exempt from the regular HISTORY_CAP trim policy, but unbounded
 *  growth is a memory-pressure vector — a user spamming
 *  Ctrl+Shift+H or a future Action Recorder programmatically
 *  pinning every step could allocate gigabytes of canvas state.
 *  When the cap is reached, the OLDEST manual snapshot is dropped
 *  to make room for the new one (FIFO eviction). Photoshop has no
 *  hard cap here; we add one because we live in a 4–8GB browser
 *  heap, not 64GB native memory. */
export const MANUAL_SNAPSHOT_CAP = 100;

export interface PushOptions {
  /** Display label. Required — callers MUST give the panel
   *  something better than "Edit" wherever they can. */
  label: string;
  /** Registry command id that produced this entry, if known. */
  commandId?: string;
  /** Pin this entry as a manual / named snapshot — cap-exempt. */
  isManual?: boolean;
}

/** The engine itself. Plain class; subscribe-able via
 *  `subscribe(listener) -> unsubscribe`. */
export class CanvasHistory<TExtra = unknown> {
  /** All entries in chronological order. Manual snapshots are
   *  interleaved with regular entries — pinning is by `isManual`
   *  flag, not by storage location. */
  private entries: HistoryEntryWithState<TExtra>[] = [];
  /** Index of the currently-applied entry. -1 when empty. */
  private index = -1;
  /** Cap on non-manual entries. Manual snapshots count separately
   *  (always kept). Constructor clamps to `>= 2`. */
  private readonly maxEntries: number;
  /** Bumped on every mutation. Subscribers re-render via
   *  `useSyncExternalStore` (see `useCanvasHistory.ts`). */
  private version = 0;
  private listeners = new Set<() => void>();
  /** Monotonic counter behind `nextId()`. Reset by `clear()`;
   *  re-seeded by `restore()` from the max id in the restored
   *  entry list so subsequent pushes don't collide. */
  private nextEntryNumber = 0;
  /** Memoized result of the last `getEntries()` call. Invalidated
   *  on every notify() so the panel sees fresh metadata, but
   *  stable across repeated reads within a single render — at 200
   *  entries × every render's read, the saved allocation matters. */
  private entriesCache: readonly HistoryEntry[] | null = null;

  constructor(maxEntries: number = HISTORY_CAP) {
    this.maxEntries = Math.max(2, maxEntries);
  }

  /** Subscribe to mutation events. Returns an unsubscribe function.
   *  Wired into React via `useSyncExternalStore` in the hook layer. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Monotonic version stamp for `useSyncExternalStore`. */
  getVersion(): number {
    return this.version;
  }

  private notify(): void {
    this.version++;
    this.entriesCache = null;
    for (const l of this.listeners) l();
  }

  private nextId(): string {
    return `h-${this.nextEntryNumber++}`;
  }

  get canUndo(): boolean {
    return this.index > 0;
  }

  get canRedo(): boolean {
    return this.entries.length > 0 && this.index < this.entries.length - 1;
  }

  get undoCount(): number {
    return this.index > 0 ? this.index : 0;
  }

  get redoCount(): number {
    return this.entries.length > 0 ? this.entries.length - 1 - this.index : 0;
  }

  /** Total entry count. */
  get size(): number {
    return this.entries.length;
  }

  /** Id of the currently-applied entry, or null when empty. The
   *  History panel uses this to highlight the current row. */
  getCurrentEntryId(): string | null {
    const entry = this.entries[this.index];
    return entry ? entry.id : null;
  }

  /** Lightweight view of the entry list — metadata only, no state
   *  payloads. The returned array is stable across repeated reads
   *  between mutations (cached + invalidated by `notify()`), so
   *  React.memo'd consumers can rely on referential equality. */
  getEntries(): readonly HistoryEntry[] {
    if (this.entriesCache === null) {
      this.entriesCache = this.entries.map(stripState);
    }
    return this.entriesCache;
  }

  /** Internal — `useCanvasHistory.getHistory()` uses this to keep
   *  the legacy session-persist shape working without re-encoding. */
  getEntriesWithState(): readonly HistoryEntryWithState<TExtra>[] {
    return this.entries;
  }

  /** Record a new state at the current head. Truncates any entries
   *  past the cursor (the fork-on-edit branch of fork-on-edit-after-
   *  jump). Bumps the cap-trim passes (regular + manual) and
   *  notifies subscribers. */
  push(snapshot: HistorySnapshot<TExtra>, opts: PushOptions): void {
    if (this.index < this.entries.length - 1) {
      this.entries = this.entries.slice(0, this.index + 1);
    }
    // For manual snapshots, FIFO-evict the oldest manual entry
    // BEFORE we append the new one if we're at the manual cap. This
    // keeps the freshly-named snapshot, which is what the user
    // expected when they pressed Ctrl+Shift+H.
    if (opts.isManual) this.enforceManualCap();
    this.entries.push({
      id: this.nextId(),
      label: opts.label,
      commandId: opts.commandId,
      isManual: !!opts.isManual,
      timestamp: Date.now(),
      state: snapshot,
    });
    this.index = this.entries.length - 1;
    this.enforceCap();
    this.notify();
  }

  /** When `MANUAL_SNAPSHOT_CAP` manual snapshots already exist,
   *  drop the OLDEST manual entry before the next manual push.
   *  Adjusts the cursor index if the dropped entry sat at or
   *  before it. */
  private enforceManualCap(): void {
    const manualEntries: number[] = [];
    for (let i = 0; i < this.entries.length; i++) {
      if (this.entries[i].isManual) manualEntries.push(i);
    }
    if (manualEntries.length < MANUAL_SNAPSHOT_CAP) return;
    const dropAt = manualEntries[0];
    this.entries.splice(dropAt, 1);
    if (this.index >= dropAt) this.index = Math.max(0, this.index - 1);
  }

  /** Trim the OLDEST non-manual entries when the cap is exceeded.
   *  The entry the user is currently sitting on is also protected —
   *  trimming it would silently desynchronize the canvas from the
   *  cursor. In normal use the cursor is at the head; this matters
   *  only when the user has jumped to an old entry. */
  private enforceCap(): void {
    const nonManual = this.entries.filter((e) => !e.isManual);
    if (nonManual.length <= this.maxEntries) return;
    let excess = nonManual.length - this.maxEntries;
    const protectedIndex = this.index;
    const next: HistoryEntryWithState<TExtra>[] = [];
    let newIndex = -1;
    for (let i = 0; i < this.entries.length; i++) {
      const e = this.entries[i];
      const drop = !e.isManual && i !== protectedIndex && excess > 0;
      if (drop) {
        excess--;
        continue;
      }
      if (i === protectedIndex) newIndex = next.length;
      next.push(e);
    }
    this.entries = next;
    // `newIndex` lands on the entry the cursor pointed at. Falls
    // back to last-entry if the cursor was somehow out of bounds.
    this.index = newIndex >= 0 ? newIndex : this.entries.length - 1;
  }

  /** Move the cursor to the entry with the given id. Returns the
   *  snapshot the caller must apply, or null when the id is unknown
   *  or already current (the latter avoids spurious cursor movement
   *  for double-clicks in the panel). */
  jumpTo(entryId: string): HistorySnapshot<TExtra> | null {
    const idx = this.entries.findIndex((e) => e.id === entryId);
    if (idx < 0) return null;
    if (idx === this.index) return null;
    this.index = idx;
    this.notify();
    return this.entries[idx].state;
  }

  undo(): HistorySnapshot<TExtra> | null {
    if (this.index <= 0) return null;
    this.index--;
    this.notify();
    return this.entries[this.index].state;
  }

  redo(): HistorySnapshot<TExtra> | null {
    if (this.index >= this.entries.length - 1) return null;
    this.index++;
    this.notify();
    return this.entries[this.index].state;
  }

  /** Wipe every entry. Used on document close / new-document. */
  clear(): void {
    if (this.entries.length === 0 && this.nextEntryNumber === 0) return;
    this.entries = [];
    this.index = -1;
    this.nextEntryNumber = 0;
    this.notify();
  }

  /** Bulk-restore from an external source (IndexedDB session
   *  reload). Two shapes accepted:
   *
   *    - Rich `HistoryEntryWithState[]` (label/commandId/isManual
   *      preserved) — current persistence schema.
   *    - Legacy `HistorySnapshot[]` (just `{ canvasJSON, extra }`)
   *      — what the IDB session-store still writes today. Labels
   *      are synthesized as "Step <N>" so the panel has SOMETHING
   *      readable until the user takes a new action. */
  restore(
    entries: readonly (HistoryEntryWithState<TExtra> | HistorySnapshot<TExtra>)[],
    index: number,
  ): void {
    const normalized: HistoryEntryWithState<TExtra>[] = entries.map((e, i) => {
      if (isFullEntry(e)) return e as HistoryEntryWithState<TExtra>;
      // Legacy snapshot — synthesize the metadata. Counter is
      // re-seeded below so future pushes won't collide.
      return {
        id: `h-${i}`,
        label: `Step ${i + 1}`,
        isManual: false,
        timestamp: Date.now(),
        state: e as HistorySnapshot<TExtra>,
      };
    });
    this.entries = normalized;
    this.index = Math.min(Math.max(index, -1), normalized.length - 1);
    // Re-seed counter past every observed id so `nextId()` doesn't
    // hand out collisions. Parses defensively — a non-matching id
    // contributes nothing.
    let maxN = -1;
    for (const e of normalized) {
      const m = /^h-(\d+)$/.exec(e.id);
      if (m) maxN = Math.max(maxN, parseInt(m[1], 10));
    }
    this.nextEntryNumber = maxN + 1;
    this.notify();
  }
}

function stripState<TExtra>(e: HistoryEntryWithState<TExtra>): HistoryEntry {
  return {
    id: e.id,
    label: e.label,
    commandId: e.commandId,
    isManual: e.isManual,
    timestamp: e.timestamp,
  };
}

function isFullEntry(value: unknown): value is HistoryEntryWithState {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'label' in value &&
    'state' in value
  );
}
